import React, { lazy, type RefObject, Suspense } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';

const WaveformAnimation = lazy(() =>
	import('./WaveformAnimation').then((mod) => ({ default: mod.WaveformAnimation })),
);

import { AudioTimeIndicator } from './AudioTimeIndicator';

const i18n = defineMessages({
	errorHeading: {
		id: 'audio.minified-audio-player.error-heading',
		defaultMessage: 'Error loading audio',
		description: 'The error message displayed when the audio fails to load',
	},
});

const styles = cssMap({
	container: {
		minWidth: '274px',
		width: '100%',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface'),
	},
	elevated: {
		width: `100%`,
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	warningIconContainer: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
	contentArea: {
		flex: '1 1 0%',
	},
});

type MinifiedAudioPlayerProps = {
	avatarUrl: string;
	byline: string;
	title: string;
	audioRef: RefObject<HTMLAudioElement>;
	// Error is a temporary prop - remove once implemented in the audio player store
	error?: Error;
	elevated?: boolean;
};

export const MinifiedAudioPlayer = ({
	title,
	byline,
	avatarUrl,
	audioRef,
	error,
	elevated,
}: MinifiedAudioPlayerProps) => {
	const { formatMessage } = useIntl();
	const { state } = useAudioPlayerStreamingStore();

	const hasFetched = state.state !== AudioStreamingState.UNINITIALIZED;

	if (error) {
		return (
			<Box xcss={styles.container}>
				<Inline alignBlock="center" space="space.100">
					<Box xcss={styles.warningIconContainer}>
						<StatusWarningIcon color={token('color.icon.warning')} label="" />
					</Box>
					<Heading size="xsmall">{formatMessage(i18n.errorHeading)}</Heading>
				</Inline>
			</Box>
		);
	}

	return (
		<Flex
			gap="space.100"
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			xcss={cx(styles.container, elevated && styles.elevated)}
		>
			<Flex gap="space.100" direction="row" alignItems="center" justifyContent="start">
				<Avatar appearance="circle" src={avatarUrl} size="medium" />
				<Box xcss={styles.contentArea}>
					<Stack space="space.025">
						<Text maxLines={1}>
							<Heading size="xxsmall">{title}</Heading>
						</Text>
						{hasFetched ? (
							<AudioTimeIndicator audioRef={audioRef} />
						) : (
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								{byline}
							</Text>
						)}
					</Stack>
				</Box>
			</Flex>
			{hasFetched && (
				<Suspense fallback={null}>
					<WaveformAnimation />
				</Suspense>
			)}
		</Flex>
	);
};
